import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaFilter,
  FaBuilding,
  FaUsers,
  FaChartBar,
  FaUserTie,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaCalendar,
  FaCheck,
  FaTimes,
  FaSave,
  FaUserPlus,
  FaUserMinus
} from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FirmManagement = () => {
  const [firms, setFirms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [selectedFirm, setSelectedFirm] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    firmName: '',
    status: 'active',
    companies: []
  });

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const itemsPerPage = 10;

  // Dropdown options
  const businessTypeOptions = [
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'trading', label: 'Trading' },
    { value: 'service', label: 'Service' },
    { value: 'retail', label: 'Retail' },
    { value: 'mixed', label: 'Mixed' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];


  useEffect(() => {
    fetchFirms();
    fetchStatistics();
    fetchCompanies();
  }, [currentPage, searchTerm]);

  const fetchFirms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/firms`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm
        }
      });
      
      if (response.data.success) {
        setFirms(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching firms:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch firms'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/firms/statistics`);
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/companies`);
        setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firmName.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Firm name is required'
      });
      return;
    }
    
    try {
      const payload = {
        firmName: formData.firmName.trim(),
        status: formData.status,
        companies: formData.companies || []
      };

      let response;
      if (editingFirm) {
        response = await axios.put(`${API_BASE_URL}/firms/${editingFirm._id}`, payload);
      } else {
        response = await axios.post(`${API_BASE_URL}/firms`, payload);
      }

      if (response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Firm ${editingFirm ? 'updated' : 'created'} successfully`,
          timer: 2000,
          showConfirmButton: false
        });
        setShowModal(false);
        resetForm();
        fetchFirms();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error saving firm:', error);
      
      const errorMessage = error.response?.data?.message || 'Failed to save firm';
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      });
    }
  };

  const handleEdit = (firm) => {
    setEditingFirm(firm);
    setFormData({
      firmName: firm.firmName || '',
      status: firm.status || 'active',
      companies: firm.companies?.map(company => company._id) || []
    });
    setShowModal(true);
  };

  const handleDelete = async (firmId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/firms/${firmId}`);
        if (response.data.success) {
          await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Firm deleted successfully',
            timer: 2000,
            showConfirmButton: false
          });
          fetchFirms();
          fetchStatistics();
        }
      } catch (error) {
        console.error('Error deleting firm:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete firm'
        });
      }
    }
  };

  const handleView = async (firmId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/firms/${firmId}`);
      if (response.data.success) {
        setSelectedFirm(response.data.data);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching firm details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch firm details'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      firmName: '',
      status: 'active',
      companies: []
    });
    setEditingFirm(null);
  };



  const StatusBadge = ({ status }) => {
    const statusConfig = {
      active: {
        bg: 'bg-gradient-to-r from-green-100 to-green-200',
        text: 'text-green-800',
        icon: <FaCheck className="w-3 h-3" />
      },
      inactive: {
        bg: 'bg-gradient-to-r from-gray-100 to-gray-200',
        text: 'text-gray-800',
        icon: <FaTimes className="w-3 h-3" />
      }
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span className={`px-4 py-2 inline-flex items-center space-x-1 text-sm font-bold rounded-full ${config.bg} ${config.text} shadow-md`}>
        {config.icon}
        <span>{status?.replace('-', ' ')?.toUpperCase()}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Firm Management
            </h1>
            <p className="text-gray-600 mt-1 text-sm">Manage firms and their associated companies with style</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <FaPlus className="w-4 h-4" />
            <span className="font-semibold text-sm">Add New Firm</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl shadow-lg border border-blue-200 hover:shadow-xl transform hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Total Firms</h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                {statistics.overview?.totalFirms || 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-md">
              <FaBuilding className="w-10 h-10 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl shadow-lg border border-green-200 hover:shadow-xl transform hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Active Firms</h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                {statistics.overview?.activeFirms || 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-md">
              <FaCheck className="w-10 h-10 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl shadow-lg border border-purple-200 hover:shadow-xl transform hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold text-gray-700 mb-1">Total Companies</h3>
              <p className="text-xl font-bold bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent">
                {statistics.overview?.totalCompanies || 0}
              </p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow-md">
              <FaUsers className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-6 rounded-2xl shadow-lg mb-8 border border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search firms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-gray-50 hover:bg-white transition-colors duration-200"
            />
          </div>
        </div>
      </div>

      {/* Firms Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <FaBuilding className="w-4 h-4" />
                    <span>Firm Details</span>
                  </div>
                </th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <FaUsers className="w-4 h-4" />
                    <span>Companies</span>
                  </div>
                </th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <FaChartBar className="w-4 h-4" />
                    <span>Status</span>
                  </div>
                </th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-16">
                    <div className="flex flex-col justify-center items-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
                      <span className="text-gray-600 font-medium text-sm">Loading firms...</span>
                    </div>
                  </td>
                </tr>
              ) : firms.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-16">
                    <div className="flex flex-col items-center">
                      <FaBuilding className="w-16 h-16 text-gray-300 mb-4" />
                      <span className="text-gray-500 text-sm font-medium">No firms found</span>
                      <span className="text-gray-400 text-sm mt-2">Try adjusting your search criteria</span>
                    </div>
                  </td>
                </tr>
              ) : (
                firms.map((firm, index) => (
                  <tr key={firm._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm">
                          {firm.firmName?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                            {firm.firmName}
                          </div>
                          <div className="text-sm text-gray-500">ID: {firm.firmCode || `FIRM-${index + 1}`}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full text-sm font-semibold">
                          {firm.companies?.length || 0} Companies
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <StatusBadge status={firm.status} />
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleView(firm._id)}
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-lg transition-all duration-200 hover:scale-110"
                          title="View Details"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(firm)}
                          className="bg-green-100 text-green-600 hover:bg-green-200 p-2 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Edit Firm"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(firm._id)}
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Delete Firm"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 shadow-md hover:shadow-lg transition-all duration-200"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 shadow-md hover:shadow-lg transition-all duration-200"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="bg-white px-4 py-2 rounded-lg shadow-md">
                <p className="text-sm text-gray-700 font-medium">
                  Page <span className="font-bold text-blue-600">{currentPage}</span> of{' '}
                  <span className="font-bold text-blue-600">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-xl shadow-lg overflow-hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-6 py-3 border border-gray-300 bg-white text-sm font-semibold text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 transition-all duration-200"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-6 py-3 border border-gray-300 bg-white text-sm font-semibold text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 transition-all duration-200"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-6xl w-full max-h-screen overflow-y-auto shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {editingFirm ? 'Edit Firm' : 'Add New Firm'}
                </h2>
                <p className="text-gray-600 mt-1 text-sm">
                  {editingFirm ? 'Update firm information' : 'Create a new firm with associated companies'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-all duration-200"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Firm Name */}
              <div className="bg-gray-50 p-4 rounded-2xl">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <FaBuilding className="w-4 h-4 text-blue-600" />
                    <span>Firm Name</span>
                    <span className="text-red-500">*</span>
                  </div>
                </label>
                <input
                  type="text"
                  required
                  value={formData.firmName}
                  onChange={(e) => setFormData({ ...formData, firmName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm hover:shadow-md transition-all duration-200"
                  placeholder="Enter firm name"
                />
              </div>

              {/* Status */}
              <div className="bg-gray-50 p-4 rounded-2xl">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <FaChartBar className="w-4 h-4 text-green-600" />
                    <span>Status</span>
                    <span className="text-red-500">*</span>
                  </div>
                </label>
                <Select
                  value={statusOptions.find(opt => opt.value === formData.status)}
                  onChange={(option) => setFormData({ ...formData, status: option.value })}
                  options={statusOptions}
                  placeholder="Select status"
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      padding: '8px',
                      borderRadius: '12px',
                      border: '1px solid #d1d5db',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }
                    })
                  }}
                />
              </div>

              {/* Companies */}
              <div className="bg-gray-50 p-4 rounded-2xl">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <FaUsers className="w-4 h-4 text-purple-600" />
                    <span>Associated Companies</span>
                  </div>
                </label>
                <Select
                  isMulti
                  value={companies.filter(company => formData.companies.includes(company._id)).map(company => ({
                    value: company._id,
                    label: company.name
                  }))}
                  onChange={(selectedOptions) => {
                    const selectedCompanyIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setFormData({ ...formData, companies: selectedCompanyIds });
                  }}
                  options={companies.map(company => ({
                    value: company._id,
                    label: company.name
                  }))}
                  placeholder="Select companies to associate with this firm"
                  className="text-lg"
                  styles={{
                    control: (base) => ({
                      ...base,
                      padding: '8px',
                      borderRadius: '12px',
                      border: '1px solid #d1d5db',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#dbeafe',
                      borderRadius: '8px'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#1e40af',
                      fontWeight: '600'
                    })
                  }}
                />
                <div className="flex items-center mt-3 text-gray-600">
                  <FaUserPlus className="w-4 h-4 mr-2" />
                  <p className="text-sm">
                    Select one or more companies to associate with this firm
                  </p>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-4 pt-8 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 font-semibold text-sm transition-all duration-200 flex items-center space-x-2"
                >
                  <FaTimes className="w-5 h-5" />
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2 font-semibold text-sm shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  <FaSave className="w-5 h-5" />
                  <span>{editingFirm ? 'Update' : 'Create'} Firm</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedFirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg">
                  {selectedFirm?.firmName?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {selectedFirm?.firmName}
                  </h2>
                  <p className="text-gray-600 text-sm">Firm Details & Information</p>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-all duration-200"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Basic Info - Simplified */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl border border-blue-200">
                <div className="flex items-center space-x-2 mb-3">
                  <FaBuilding className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-800">Firm Information</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <span className="font-semibold text-gray-700">Firm Name:</span>
                    <span className="text-gray-900 font-semibold">{selectedFirm.firmName}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <span className="font-semibold text-gray-700">Status:</span>
                    <StatusBadge status={selectedFirm.status} />
                  </div>
                </div>
              </div>

              {/* Companies */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-2xl border border-orange-200">
                <div className="flex items-center space-x-2 mb-3">
                  <FaUsers className="w-5 h-5 text-orange-600" />
                  <h3 className="text-base font-bold text-gray-800">Associated Companies</h3>
                </div>
                {selectedFirm.companies && selectedFirm.companies.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto space-y-2 pr-2">
                    {selectedFirm.companies.map((company, index) => (
                      <div key={company._id || index} className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center space-x-3">
                          <div className="bg-orange-500 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs">
                            {index + 1}
                          </div>
                          <span className="font-semibold text-gray-700 text-sm">
                            {company.name || company.companyName || `Company ${index + 1}`}
                          </span>
                        </div>
                        <StatusBadge status={company.status || 'active'} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-lg text-center">
                    <FaUsers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium text-sm">No companies associated</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirmManagement;